import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  X,
  Upload,
  Save,
  Trash2,
  GripVertical,
  Video,
  Youtube,
  Loader2,
  Star
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { toast } from 'sonner';

const TIPOS = ['Autos', 'Camionetas', 'Tractocamiones', 'Motos', 'Remolques'];
const COLORES = ['Blanco', 'Negro', 'Gris', 'Plata', 'Rojo', 'Azul', 'Verde', 'Amarillo', 'Naranja', 'Café', 'Arena', 'Beige'];
const TRANSMISIONES = ['Automático', 'Manual', 'CVT', 'Semiautomático'];
const CONDICIONES = ['Chocado', 'Funcionando', 'Seminuevo', 'Para partes', 'Chatarra'];
const COMBUSTIBLES = ['Gasolina', 'Diesel', 'Híbrido', 'Eléctrico', 'Gas LP'];


const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 35 }, (_, i) => currentYear - i + 1);

export default function AdminVehicleForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [categories, setCategories] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [marcaDialogOpen, setMarcaDialogOpen] = useState(false);
  const [newMarcaNombre, setNewMarcaNombre] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [addingYoutube, setAddingYoutube] = useState(false);
  
  const [formData, setFormData] = useState({
    titulo: '',
    precio: '',
    descripcion: '',
    categoria_id: [],
    tipo: '',
    marca: '',
    modelo: currentYear,
    color: '',
    whatsapp_numbers: [
      { number: '33 2548 3444', label: 'Ventas 1' },
      { number: '33 1456 6426', label: 'Ventas 2' },
      { number: '33 3058 0006', label: 'Ventas 3' }
    ],
    imagenes: [],
    videos: [],
    motor: '',
    transmision: '',
    kilometraje: '',
    combustible: '',
    numero_llave: '',
    condicion: '',
    destacado: false,
    oferta: false,
    fecha_publicacion: ''
  });

  // Fetch categories and marcas
  useEffect(() => {
    const fetchData = async () => {
      try {
        const cats = await api.getCategories();
        setCategories(Array.isArray(cats) ? cats : []);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
      try {
        const marcasData = await api.getMarcas();
        setMarcas(Array.isArray(marcasData) ? marcasData : []);
      } catch (error) {
        console.error('Error fetching marcas:', error);
      }
    };
    fetchData();
  }, []);

  // Fetch vehicle data if editing
  useEffect(() => {
    if (isEditing) {
      const fetchVehicle = async () => {
        setLoading(true);
        try {
          const data = await api.getVehicle(id);
          setFormData({
            titulo: data.titulo || '',
            precio: data.precio?.toString() || '',
            descripcion: data.descripcion || '',
            categoria_id: Array.isArray(data.categoria_id) ? data.categoria_id : data.categoria_id ? [data.categoria_id] : [],
            tipo: data.tipo || '',
            marca: data.marca || '',
            modelo: data.modelo || currentYear,
            color: data.color || '',
            whatsapp_numbers: data.whatsapp_numbers?.length > 0 
              ? data.whatsapp_numbers 
              : [{ number: '', label: '' }],
            imagenes: data.imagenes || [],
            videos: data.videos || [],
            motor: data.motor || '',
            transmision: data.transmision || '',
            kilometraje: data.kilometraje?.toString() || '',
            combustible: data.combustible || '',
            numero_llave: data.numero_llave || '',
            condicion: data.condicion || '',
            destacado: data.destacado || false,
            oferta: data.oferta || false,
            fecha_publicacion: data.updated_at ? data.updated_at.split('T')[0] : ''
          });
        } catch (error) {
          console.error('Error fetching vehicle:', error);
          toast.error('Error al cargar el vehículo');
          navigate('/admin');
        } finally {
          setLoading(false);
        }
      };
      fetchVehicle();
    }
  }, [id, isEditing, navigate]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleWhatsAppChange = (index, field, value) => {
    const newNumbers = [...formData.whatsapp_numbers];
    newNumbers[index] = { ...newNumbers[index], [field]: value };
    setFormData(prev => ({ ...prev, whatsapp_numbers: newNumbers }));
  };

  const addWhatsAppNumber = () => {
    setFormData(prev => ({
      ...prev,
      whatsapp_numbers: [...prev.whatsapp_numbers, { number: '', label: '' }]
    }));
  };

  const removeWhatsAppNumber = (index) => {
    if (formData.whatsapp_numbers.length > 1) {
      setFormData(prev => ({
        ...prev,
        whatsapp_numbers: prev.whatsapp_numbers.filter((_, i) => i !== index)
      }));
    }
  };

  const uploadImages = async (files) => {
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    if (imageFiles.length === 0) return;

    setUploading(true);
    try {
      const result = await api.uploadImages(imageFiles);
      setFormData(prev => ({
        ...prev,
        imagenes: [...prev.imagenes, ...result.urls]
      }));
      toast.success(`${result.urls.length} imagen(es) subida(s) exitosamente`);
    } catch (error) {
      console.error('Error uploading images:', error);
      toast.error('Error al subir las imágenes');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleImageUpload = (e) => {
    uploadImages(Array.from(e.target.files));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    uploadImages(Array.from(e.dataTransfer.files));
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check size client-side
    if (file.size > 100 * 1024 * 1024) {
      toast.error('El video excede el límite de 100MB');
      return;
    }

    setUploadingVideo(true);
    try {
      const result = await api.uploadVideo(file);
      setFormData(prev => ({
        ...prev,
        videos: [...prev.videos, { url: result.url, tipo: 'local', thumbnail: null }]
      }));
      toast.success('Video subido exitosamente');
    } catch (error) {
      console.error('Error uploading video:', error);
      toast.error(error.response?.data?.detail || 'Error al subir el video');
    } finally {
      setUploadingVideo(false);
      if (videoInputRef.current) {
        videoInputRef.current.value = '';
      }
    }
  };

  const handleAddYoutubeVideo = async () => {
    if (!youtubeUrl.trim()) {
      toast.error('Ingresa una URL de YouTube');
      return;
    }

    setAddingYoutube(true);
    try {
      const result = await api.parseYoutubeUrl(youtubeUrl);
      setFormData(prev => ({
        ...prev,
        videos: [...prev.videos, { url: result.url, tipo: 'youtube', thumbnail: result.thumbnail }]
      }));
      setYoutubeUrl('');
      toast.success('Video de YouTube agregado');
    } catch (error) {
      console.error('Error parsing YouTube URL:', error);
      toast.error(error.response?.data?.detail || 'URL de YouTube inválida');
    } finally {
      setAddingYoutube(false);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      imagenes: prev.imagenes.filter((_, i) => i !== index)
    }));
  };

  const removeVideo = (index) => {
    setFormData(prev => ({
      ...prev,
      videos: prev.videos.filter((_, i) => i !== index)
    }));
  };

  const moveImage = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    const newImages = [...formData.imagenes];
    const [removed] = newImages.splice(fromIndex, 1);
    newImages.splice(toIndex, 0, removed);
    setFormData(prev => ({ ...prev, imagenes: newImages }));
  };

  const setAsMainImage = (index) => {
    if (index === 0) return;
    moveImage(index, 0);
    toast.success('Imagen establecida como principal');
  };

  // Drag & drop reorder state
  const dragImageIndex = useRef(null);
  const dragOverImageIndex = useRef(null);

  const handleImageDragStart = (e, index) => {
    dragImageIndex.current = index;
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.4';
  };

  const handleImageDragEnter = (e, index) => {
    e.preventDefault();
    dragOverImageIndex.current = index;
    e.currentTarget.classList.add('ring-2', 'ring-primary');
  };

  const handleImageDragLeave = (e) => {
    e.currentTarget.classList.remove('ring-2', 'ring-primary');
  };

  const handleImageDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleImageDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    document.querySelectorAll('.image-sortable').forEach(el => {
      el.classList.remove('ring-2', 'ring-primary');
    });
    if (dragImageIndex.current !== null && dragOverImageIndex.current !== null) {
      moveImage(dragImageIndex.current, dragOverImageIndex.current);
    }
    dragImageIndex.current = null;
    dragOverImageIndex.current = null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.titulo || !formData.precio || !formData.categoria_id?.length || !formData.tipo || !formData.marca || !formData.color) {
      toast.error('Por favor completa los campos requeridos');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        precio: parseFloat(formData.precio),
        modelo: parseInt(formData.modelo),
        kilometraje: formData.kilometraje ? parseInt(formData.kilometraje) : null,
        whatsapp_numbers: formData.whatsapp_numbers.filter(wa => wa.number.trim() !== ''),
        fecha_publicacion: formData.fecha_publicacion || undefined
      };

      if (isEditing) {
        await api.updateVehicle(id, payload);
        toast.success('Vehículo actualizado exitosamente');
      } else {
        await api.createVehicle(payload);
        toast.success('Vehículo creado exitosamente');
      }
      
      navigate('/admin');
    } catch (error) {
      console.error('Error saving vehicle:', error);
      toast.error('Error al guardar el vehículo');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <div className="flex items-center justify-between h-16">
            <Link 
              to="/admin"
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Volver al Dashboard</span>
            </Link>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className="btn-primary"
              data-testid="save-vehicle-btn"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></span>
                  Guardando...
                </span>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isEditing ? 'Actualizar' : 'Crear'} Vehículo
                </>
              )}
            </Button>
          </div>
        </div>
      </header>

      {/* Form */}
      <main className="container mx-auto px-4 md:px-6 max-w-5xl py-8">
        <h1 className="text-2xl font-bold uppercase tracking-wider mb-8">
          {isEditing ? 'Editar Vehículo' : 'Nuevo Vehículo'}
        </h1>

        <form onSubmit={handleSubmit} className="space-y-8" data-testid="vehicle-form">
          {/* Basic Info */}
          <section className="bg-card border border-border rounded-sm p-6 space-y-6">
            <h2 className="text-lg font-bold uppercase tracking-wider border-b border-border pb-4">
              Información Básica
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="titulo">Título *</Label>
                <Input
                  id="titulo"
                  value={formData.titulo}
                  onChange={(e) => handleChange('titulo', e.target.value)}
                  placeholder="Ej: MERCEDES BENZ CLA200 2022.. LLAVE 9851.."
                  className="bg-input border-transparent focus:border-primary"
                  required
                  data-testid="input-titulo"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="precio">Precio (MXN) *</Label>
                <Input
                  id="precio"
                  type="number"
                  value={formData.precio}
                  onChange={(e) => handleChange('precio', e.target.value)}
                  placeholder="498000"
                  className="bg-input border-transparent focus:border-primary"
                  required
                  data-testid="input-precio"
                />
              </div>

              <div className="space-y-2">
                <Label>Categorías *</Label>
                <div className="space-y-2 bg-input rounded-md p-3" data-testid="select-categoria">
                  {categories.map(cat => (
                    <label key={cat.id} className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={(formData.categoria_id || []).includes(cat.id)}
                        onCheckedChange={(checked) => {
                          const current = formData.categoria_id || [];
                          const next = checked
                            ? [...current, cat.id]
                            : current.filter(id => id !== cat.id);
                          handleChange('categoria_id', next);
                        }}
                      />
                      <span className="text-sm">{cat.nombre}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tipo">Tipo *</Label>
                <Select value={formData.tipo} onValueChange={(v) => handleChange('tipo', v)}>
                  <SelectTrigger className="bg-input border-transparent" data-testid="select-tipo">
                    <SelectValue placeholder="Selecciona el tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS.map(tipo => (
                      <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="marca">Marca *</Label>
                <div className="flex gap-2">
                  <Select value={formData.marca} onValueChange={(v) => handleChange('marca', v)}>
                    <SelectTrigger className="bg-input border-transparent flex-1" data-testid="select-marca">
                      <SelectValue placeholder="Selecciona la marca" />
                    </SelectTrigger>
                    <SelectContent>
                      {marcas.map(m => (
                        <SelectItem key={m.id} value={m.nombre}>{m.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button type="button" variant="outline" size="icon" onClick={() => setMarcaDialogOpen(true)} title="Nueva marca">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="modelo">Año/Modelo *</Label>
                <Select value={formData.modelo.toString()} onValueChange={(v) => handleChange('modelo', parseInt(v))}>
                  <SelectTrigger className="bg-input border-transparent" data-testid="select-modelo">
                    <SelectValue placeholder="Selecciona el año" />
                  </SelectTrigger>
                  <SelectContent>
                    {YEARS.map(year => (
                      <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="color">Color *</Label>
                <Select value={formData.color} onValueChange={(v) => handleChange('color', v)}>
                  <SelectTrigger className="bg-input border-transparent" data-testid="select-color">
                    <SelectValue placeholder="Selecciona el color" />
                  </SelectTrigger>
                  <SelectContent>
                    {COLORES.map(color => (
                      <SelectItem key={color} value={color}>{color}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="md:col-span-2 space-y-2">
                <Label htmlFor="descripcion">Descripción</Label>
                <Textarea
                  id="descripcion"
                  value={formData.descripcion}
                  onChange={(e) => handleChange('descripcion', e.target.value)}
                  placeholder="ID 35688&#10;&#10;MERCEDES BENZ CLA200 2022 NEGRO, AIRE, DIRECCION, ELECTRICO..."
                  className="bg-input border-transparent focus:border-primary min-h-[120px]"
                  data-testid="input-descripcion"
                />
              </div>
            </div>
          </section>

          {/* Optional Details */}
          <section className="bg-card border border-border rounded-sm p-6 space-y-6">
            <h2 className="text-lg font-bold uppercase tracking-wider border-b border-border pb-4">
              Detalles Opcionales
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label htmlFor="kilometraje">Kilometraje</Label>
                <Input
                  id="kilometraje"
                  type="number"
                  value={formData.kilometraje}
                  onChange={(e) => handleChange('kilometraje', e.target.value)}
                  placeholder="31000"
                  className="bg-input border-transparent focus:border-primary"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="transmision">Transmisión</Label>
                <Select value={formData.transmision} onValueChange={(v) => handleChange('transmision', v)}>
                  <SelectTrigger className="bg-input border-transparent">
                    <SelectValue placeholder="Selecciona" />
                  </SelectTrigger>
                  <SelectContent>
                    {TRANSMISIONES.map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="combustible">Combustible</Label>
                <Select value={formData.combustible} onValueChange={(v) => handleChange('combustible', v)}>
                  <SelectTrigger className="bg-input border-transparent">
                    <SelectValue placeholder="Selecciona" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMBUSTIBLES.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="condicion">Condición</Label>
                <Select value={formData.condicion} onValueChange={(v) => handleChange('condicion', v)}>
                  <SelectTrigger className="bg-input border-transparent">
                    <SelectValue placeholder="Selecciona" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONDICIONES.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="motor">Motor</Label>
                <Input
                  id="motor"
                  value={formData.motor}
                  onChange={(e) => handleChange('motor', e.target.value)}
                  placeholder="2.0L Turbo"
                  className="bg-input border-transparent focus:border-primary"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="numero_llave">No. de Llave</Label>
                <Input
                  id="numero_llave"
                  value={formData.numero_llave}
                  onChange={(e) => handleChange('numero_llave', e.target.value)}
                  placeholder="9851"
                  className="bg-input border-transparent focus:border-primary"
                />
              </div>
            </div>

            <div className="flex items-center gap-4 pt-4 border-t border-border">
              <Switch
                id="destacado"
                checked={formData.destacado}
                onCheckedChange={(checked) => handleChange('destacado', checked)}
              />
              <Label htmlFor="destacado" className="cursor-pointer">
                Marcar como destacado (aparece primero en el listado)
              </Label>
            </div>

            <div className="flex items-center gap-4 pt-4 border-t border-border">
              <Switch
                id="oferta"
                checked={formData.oferta}
                onCheckedChange={(checked) => handleChange('oferta', checked)}
              />
              <Label htmlFor="oferta" className="cursor-pointer">
                Marcar como oferta (muestra badge de oferta en el sitio)
              </Label>
            </div>

            <div className="pt-4 border-t border-border">
              <div className="space-y-2 max-w-xs">
                <Label htmlFor="fecha_publicacion">Fecha de Publicacion</Label>
                <Input
                  id="fecha_publicacion"
                  type="date"
                  value={formData.fecha_publicacion}
                  onChange={(e) => handleChange('fecha_publicacion', e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="bg-input border-transparent focus:border-primary"
                />
                <p className="text-xs text-muted-foreground">
                  Si se deja vacio, se usa la fecha actual al guardar.
                </p>
              </div>
            </div>
          </section>

          {/* WhatsApp Numbers */}
          <section className="bg-card border border-border rounded-sm p-6 space-y-6">
            <div className="flex items-center justify-between border-b border-border pb-4">
              <h2 className="text-lg font-bold uppercase tracking-wider">
                Números de WhatsApp
              </h2>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addWhatsAppNumber}
                className="border-primary text-primary hover:bg-primary hover:text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Agregar
              </Button>
            </div>
            
            <div className="space-y-4">
              {formData.whatsapp_numbers.map((wa, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      value={wa.number}
                      onChange={(e) => handleWhatsAppChange(index, 'number', e.target.value)}
                      placeholder="33 2548 3444"
                      className="bg-input border-transparent focus:border-primary"
                      data-testid={`whatsapp-number-${index}`}
                    />
                    <Input
                      value={wa.label}
                      onChange={(e) => handleWhatsAppChange(index, 'label', e.target.value)}
                      placeholder="Etiqueta (ej: Ventas)"
                      className="bg-input border-transparent focus:border-primary"
                    />
                  </div>
                  {formData.whatsapp_numbers.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeWhatsAppNumber(index)}
                      className="text-destructive hover:text-destructive flex-shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Media (Images & Videos) */}
          <section className="bg-card border border-border rounded-sm p-6 space-y-6">
            <h2 className="text-lg font-bold uppercase tracking-wider border-b border-border pb-4">
              Multimedia
            </h2>
            
            <Tabs defaultValue="images" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="images" className="flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  Imágenes ({formData.imagenes.length})
                </TabsTrigger>
                <TabsTrigger value="videos" className="flex items-center gap-2">
                  <Video className="w-4 h-4" />
                  Videos ({formData.videos.length})
                </TabsTrigger>
              </TabsList>

              {/* Images Tab */}
              <TabsContent value="images" className="space-y-6">
                {/* Upload Area */}
                <div
                  className={`border-2 border-dashed rounded-sm p-8 text-center cursor-pointer transition-colors ${dragOver ? 'border-primary bg-primary/10' : 'border-border hover:border-primary'}`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    data-testid="image-upload"
                  />
                  <Upload className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground mb-2">
                    {uploading ? 'Subiendo imágenes...' : dragOver ? 'Suelta las imágenes aquí' : 'Arrastra imágenes aquí o haz clic para seleccionar'}
                  </p>
                  <p className="text-sm text-muted-foreground/60">
                    JPG, PNG, WEBP hasta 20MB cada una
                  </p>
                </div>

                {/* Image Grid - Drag & Drop */}
                {formData.imagenes.length > 0 && (
                  <>
                    <p className="text-sm text-muted-foreground">
                      Arrastra las imágenes para reordenarlas. La primera imagen es la principal.
                    </p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {formData.imagenes.map((img, index) => {
                        const imageUrl = img.startsWith('/api')
                          ? `${process.env.REACT_APP_BACKEND_URL}${img}`
                          : img;

                        return (
                          <div
                            key={img}
                            draggable
                            onDragStart={(e) => handleImageDragStart(e, index)}
                            onDragEnter={(e) => handleImageDragEnter(e, index)}
                            onDragLeave={handleImageDragLeave}
                            onDragOver={handleImageDragOver}
                            onDragEnd={handleImageDragEnd}
                            className="image-sortable relative group aspect-video bg-muted rounded overflow-hidden cursor-grab active:cursor-grabbing transition-all"
                          >
                            <img
                              src={imageUrl}
                              alt={`Imagen ${index + 1}`}
                              className="w-full h-full object-cover pointer-events-none"
                              onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/300x200?text=Error';
                              }}
                            />

                            {/* Drag handle indicator */}
                            <div className="absolute top-2 right-2 bg-black/60 text-white rounded p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <GripVertical className="w-4 h-4" />
                            </div>

                            {/* Overlay with actions */}
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center gap-2 pb-3">
                              {index !== 0 && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => { e.stopPropagation(); setAsMainImage(index); }}
                                  className="text-white hover:bg-primary/80 text-xs h-7 px-2"
                                  title="Establecer como principal"
                                >
                                  <Star className="w-3 h-3 mr-1" />
                                  Principal
                                </Button>
                              )}
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); removeImage(index); }}
                                className="text-white hover:bg-destructive text-xs h-7 px-2"
                                title="Eliminar"
                              >
                                <Trash2 className="w-3 h-3 mr-1" />
                                Eliminar
                              </Button>
                            </div>

                            {/* Badge for first image */}
                            {index === 0 && (
                              <div className="absolute top-2 left-2 bg-primary text-white text-xs font-bold px-2 py-1 rounded flex items-center gap-1">
                                <Star className="w-3 h-3 fill-current" />
                                Principal
                              </div>
                            )}

                            {/* Position number */}
                            <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs font-bold w-6 h-6 rounded-full flex items-center justify-center">
                              {index + 1}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </TabsContent>

              {/* Videos Tab */}
              <TabsContent value="videos" className="space-y-6">
                {/* Video Upload Options */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Upload Local Video */}
                  <div 
                    className="border-2 border-dashed border-border rounded-sm p-6 text-center cursor-pointer hover:border-primary transition-colors"
                    onClick={() => videoInputRef.current?.click()}
                  >
                    <input
                      ref={videoInputRef}
                      type="file"
                      accept="video/*"
                      onChange={handleVideoUpload}
                      className="hidden"
                      data-testid="video-upload"
                    />
                    {uploadingVideo ? (
                      <Loader2 className="w-10 h-10 text-primary mx-auto mb-3 animate-spin" />
                    ) : (
                      <Video className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                    )}
                    <p className="text-sm font-medium text-foreground mb-1">
                      {uploadingVideo ? 'Subiendo video...' : 'Subir Video'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      MP4, WebM, MOV (máx. 100MB)
                    </p>
                  </div>

                  {/* Add YouTube Video */}
                  <div className="border-2 border-dashed border-border rounded-sm p-6">
                    <Youtube className="w-10 h-10 text-red-500 mx-auto mb-3" />
                    <p className="text-sm font-medium text-foreground mb-3 text-center">
                      Agregar Video de YouTube
                    </p>
                    <div className="flex gap-2">
                      <Input
                        value={youtubeUrl}
                        onChange={(e) => setYoutubeUrl(e.target.value)}
                        placeholder="https://youtube.com/watch?v=..."
                        className="bg-input border-transparent focus:border-primary text-sm"
                        data-testid="youtube-url"
                      />
                      <Button
                        type="button"
                        onClick={handleAddYoutubeVideo}
                        disabled={addingYoutube}
                        size="sm"
                        className="bg-red-500 hover:bg-red-600"
                      >
                        {addingYoutube ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Video Grid */}
                {formData.videos.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {formData.videos.map((video, index) => (
                      <div 
                        key={index} 
                        className="relative group aspect-video bg-muted rounded overflow-hidden"
                      >
                        {video.tipo === 'youtube' ? (
                          <>
                            <img
                              src={video.thumbnail || `https://img.youtube.com/vi/${video.url.split('/').pop()}/maxresdefault.jpg`}
                              alt={`Video ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="w-16 h-16 rounded-full bg-red-500/90 flex items-center justify-center">
                                <Youtube className="w-8 h-8 text-white" />
                              </div>
                            </div>
                          </>
                        ) : (
                          <video
                            src={video.url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${video.url}` : video.url}
                            className="w-full h-full object-cover"
                          />
                        )}
                        
                        {/* Delete Overlay */}
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeVideo(index)}
                            className="text-white hover:bg-destructive"
                            title="Eliminar"
                          >
                            <Trash2 className="w-6 h-6" />
                          </Button>
                        </div>

                        {/* Type Badge */}
                        <div className={`absolute top-2 left-2 text-white text-xs font-bold px-2 py-1 rounded ${video.tipo === 'youtube' ? 'bg-red-500' : 'bg-primary'}`}>
                          {video.tipo === 'youtube' ? 'YouTube' : 'Local'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </section>

          {/* Submit Button (Mobile) */}
          <div className="lg:hidden">
            <Button
              type="submit"
              disabled={saving}
              className="w-full btn-primary"
            >
              {saving ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Crear') + ' Vehículo'}
            </Button>
          </div>
        </form>
      </main>
      {/* Dialog crear marca inline */}
      <Dialog open={marcaDialogOpen} onOpenChange={setMarcaDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle>Nueva Marca</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              value={newMarcaNombre}
              onChange={(e) => setNewMarcaNombre(e.target.value)}
              placeholder="Nombre de la marca"
              className="bg-input border-transparent focus:border-primary"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  document.getElementById('create-marca-btn')?.click();
                }
              }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setMarcaDialogOpen(false); setNewMarcaNombre(''); }}>Cancelar</Button>
            <Button
              id="create-marca-btn"
              className="btn-primary"
              onClick={async () => {
                if (!newMarcaNombre.trim()) { toast.error('El nombre es requerido'); return; }
                try {
                  const created = await api.createMarca({ nombre: newMarcaNombre.trim() });
                  setMarcas(prev => [...prev, created].sort((a, b) => a.nombre.localeCompare(b.nombre)));
                  handleChange('marca', created.nombre);
                  setMarcaDialogOpen(false);
                  setNewMarcaNombre('');
                  toast.success('Marca creada');
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Error al crear la marca');
                }
              }}
            >
              Crear
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
